/* eslint-disable max-lines */
import { Location, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Inject,
  LOCALE_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { combineLatest, merge } from 'rxjs';
import { filter, first, last, map, switchMap } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account-data';
import type { BillingEstimate } from '@recruitee/billing-estimate-types';
import type { BillingInformation } from '@recruitee/billing-information-types';
import { getProductCodename } from '@recruitee/billing-plans';
import { BillingPlansSelectors } from '@recruitee/billing-plans-data';
import { filterBoolean, trackByIndex } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { SubscriptionPricingActions, SubscriptionPricingSelectors } from '@recruitee/pricing-data';
import type { BillingPlan, Price, BillingPlanTier } from '@recruitee/products-types';
import type { PaymentInterval, Subscription } from '@recruitee/subscription-types';
import { AddonType } from '@recruitee/subscription-types';
import {
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogRef,
  DividerComponent,
  MessageComponent,
  NotificationsController,
  RadioComponent,
  SpinnerComponent,
} from '@recruitee/user-interface';

import { formatPrice } from '../../../helpers/format-price';
import { get2024ProductCosts } from '../../../helpers/get-2024-product-costs';
import { getErrorMessage } from '../../../helpers/get-error-message';
import { getProductCost } from '../../../helpers/get-product-cost';
import { getProductName } from '../../../helpers/get-product-name';
import { isPp24 } from '../../../helpers/is-pp24';
import { ProductOrderEstimateService } from '../../../services/product-order-estimate.service';
import { CreditCardComponent } from '../../../shared/credit-card/credit-card.component';
import { StripeCardComponent } from '../../../shared/stripe/stripe-card/stripe-card.component';
import type { PricingFeatureLoadedModel } from '../../../types/pricing-feature.types';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../../../types/pricing-feature.types';
import type { StripeElements } from '../../../types/stripe.interfaces';
import { ProductOrderConfigService } from '../config/product-order-config.service';
import type { Breadcrumb } from '../product-order-header/product-order-header.component';
import { ProductOrderHeaderComponent } from '../product-order-header/product-order-header.component';
import { ProductOrderSummaryComponent } from '../product-order-summary/product-order-summary.component';
import type { ProductOrderParams } from '../product-order.types';
import { ProductOrderParamsIT, ProductOrderStepUrl } from '../product-order.types';

export type PaymentMethodFlow = 'pristine' | 'reuse' | 'change';

interface Context {
  params: ProductOrderParams;
  model: PricingFeatureLoadedModel;
  hasSampleData: boolean;
  billingPlans: BillingPlan[];
  activeBillingPlan: BillingPlan;
  company: { name: string; id: number };
  estimate: BillingEstimate;
  subscription: Subscription;
  sizeRange: string;
}

type Confirmation = {
  value: boolean;
  label: string;
};

type ManualChangeMode = 'upgrade' | 'renewal' | null;

@Component({
  selector: 'rt-product-order-payment-step',
  templateUrl: './product-order-payment-step.component.html',
  styleUrls: ['./product-order-payment-step.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProductOrderHeaderComponent,
    ProductOrderSummaryComponent,
    BoxComponent,
    RadioComponent,
    CreditCardComponent,
    NgTemplateOutlet,
    StripeCardComponent,
    MessageComponent,
    DividerComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    ButtonComponent,
    ButtonLoadingDirective,
    SpinnerComponent,
    TranslatePipe,
  ],
})
export class ProductOrderPaymentStepComponent {
  public readonly trackByIndex = trackByIndex;
  public slots: number | undefined;
  public interval: PaymentInterval;
  public product: BillingPlan;
  public price: Price | undefined;
  public billingInformation: Partial<BillingInformation>;
  public subscription: Subscription | undefined;
  public activeBillingPlan: BillingPlan | undefined;
  public errorMessage: string;
  public billingEstimate: BillingEstimate;

  public headingText: string;
  public ctaBtnText: string;
  public altCtaBtnText: string;

  public hasCreditCardData: boolean;
  public flow: PaymentMethodFlow;

  public proceeding: boolean;
  public breadcrumbs: Breadcrumb[];

  public productCost: number;
  public companyName: string;
  public companyId: number;
  public confirmations: Confirmation[] = [];
  public manualChangeMode: ManualChangeMode = null;
  public manualTermsGeneral = '';
  public additionalTermsExpanded = signal(false);
  public additionalTermsText = '';
  public payLaterEnabled = false;

  private hasSampleData: boolean = false;
  private stripeFormComplete?: boolean;
  private stripeFormValue?: StripeElements;

  private skipRedirectOnSuccess: boolean;
  private isTrial: boolean;
  private isDowngrade: boolean;
  private sizeRange: string;
  private maxEmployees?: string;
  private isPP24Checkout: boolean;
  private params$ = inject(ProductOrderParamsIT);

  constructor(
    private cdRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private notify: NotificationsController,
    private router: Router,
    private facade: PricingFeatureFacade,
    private model$: PricingFeatureObservableModel,
    private i18n: I18nService,
    private location: Location,
    private store: Store,
    @Inject(LOCALE_ID) private locale: string,
    private config: ProductOrderConfigService,
    private dialog: DialogRef,
    private actions$: Actions,
    private estimate: ProductOrderEstimateService,
    private destroyRef: DestroyRef,
  ) {}

  public get isConfirmed(): boolean {
    return this.confirmations.every(confirm => confirm.value);
  }

  public get isValid(): boolean {
    if (!this.isConfirmed) return false;

    if (this.hasCreditCardData && this.flow === 'reuse') {
      return true;
    }

    if (this.flow !== 'reuse') {
      return !!this.stripeFormComplete;
    }

    return false;
  }

  public selectFlow(flow: PaymentMethodFlow) {
    this.flow = flow;
    this.updateState();
  }

  // eslint-disable-next-line max-lines-per-function
  public next(payLater = false) {
    this.proceeding = true;
    this.cdRef.detectChanges();
    const addons = this.sizeRange
      ? []
      : this.slots !== undefined
        ? [{ type: AddonType.Slots, quantity: this.slots }]
        : [];
    const isChangingPlan =
      !this.activeBillingPlan || this.activeBillingPlan?.tier !== this.product.tier;
    const isChangingPeriod =
      !this.activeBillingPlan || this.activeBillingPlan.billingPeriod !== +this.interval;
    const isSwitchingToCustom =
      !this.activeBillingPlan || (!this.activeBillingPlan.custom && this.product.custom);
    const billingPlanId =
      isChangingPlan || isChangingPeriod || isSwitchingToCustom
        ? this.product.id
        : this.activeBillingPlan?.id!;

    if (payLater) {
      this.facade
        .patchSubscription(billingPlanId, <1 | 12>this.interval, addons, null, this.sizeRange)
        .subscribe(_ => this.onSuccess());
      return;
    }

    // eslint-disable-next-line default-case
    switch (this.flow) {
      case 'reuse':
        this.facade
          .paymentIntent(billingPlanId, <PaymentInterval>+this.interval, addons, this.sizeRange)
          .subscribe(_ => this.onSuccess());
        break;

      case 'pristine':
      case 'change':
        this.facade
          .updatePaymentMethodData(this.billingInformation, this.stripeFormValue!, false)
          .pipe(
            switchMap(() =>
              this.facade.paymentIntent(
                billingPlanId,
                <PaymentInterval>+this.interval,
                addons,
                this.sizeRange,
              ),
            ),
          )
          .pipe(last())
          .subscribe(_ => this.onSuccess());
        break;
    }
  }

  public cardFormChange(stripeFormValue: StripeElements) {
    this.stripeFormValue = stripeFormValue;
    this.stripeFormComplete = stripeFormValue.complete;
    this.updateState();
  }

  public get codename(): BillingPlanTier {
    return getProductCodename(this.product);
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit() {
    const estimate$ = this.estimate.getData$();

    combineLatest({
      params: this.params$,
      model: this.model$.pipe(filter<PricingFeatureLoadedModel>(({ loaded }) => loaded)),
      hasSampleData: this.facade.hasSampleData(),
      billingPlans: this.facade.getBillingPlansWithActive(),
      activeBillingPlan: this.facade.getActiveBillingPlan(),
      currentCompany: this.store.select(AccountSelectors.currentCompany).pipe(filterBoolean),
      estimate: estimate$,
      subscription: this.store.select(SubscriptionPricingSelectors.data),
      sizeRange: this.params$.pipe(
        switchMap(params =>
          this.store.select(BillingPlansSelectors.rangeForQuantity(params.quantity)),
        ),
      ),
    })
      .pipe(
        map(
          data =>
            <Context>{
              ...data,
              company: { name: data.currentCompany.name, id: data.currentCompany.id },
            },
        ),
        takeUntilDestroyed(this.destroyRef),
        first(),
      )
      .subscribe((context: Context) => {
        this.onContextUpdate(context);
        this.hasSampleData = context.hasSampleData;
      });
    this.skipRedirectOnSuccess = !!this.config.setup.skipRedirectOnSuccess;
    this.handleErrors();
  }

  public back() {
    this.router.navigate(['../', ProductOrderStepUrl.OrderBilling], {
      relativeTo: this.route,
      state: {
        downgradeScreenViewed: this.downgradeScreenViewed(),
        migrationScreenViewed: this.migrationScreenViewed(),
      },
    });
  }

  public toggleCheckbox(index: number) {
    if (!this.confirmations[index]) return;
    this.confirmations = this.confirmations.map((checkbox, _index) => {
      if (index !== _index) return checkbox;
      return {
        ...checkbox,
        value: !checkbox.value,
      };
    });
    this.cdRef.detectChanges();
  }

  private onSuccess() {
    const oldProductId = this.activeBillingPlan?.id;
    const interval = this.subscription?.billingPeriod;

    if (oldProductId === this.product.id) {
      if (this.skipRedirectOnSuccess) this.orderCompletedNoRedirect();
      else this.facade.orderCompletedSuccessfully(this.hasSampleData, false);

      if (+this.interval !== interval) {
        this.notify.success(this.i18n.translate('app.pricing.plan_interval_updated'));
      }
      if (this.slots !== this.subscription?.slots) {
        this.notify.success(this.i18n.translate('app.pricing.job_slots_updated'));
      }
    } else if (!this.router.url.includes('pricing/trial')) {
      if (this.skipRedirectOnSuccess) this.orderCompletedNoRedirect();
      else this.facade.orderCompletedSuccessfully(this.hasSampleData, true);
    }
  }

  private onError(error: any) {
    this.errorMessage = this.extractErrorMessage(error);
    this.proceeding = false;
    this.cdRef.detectChanges();
  }

  private orderCompletedNoRedirect() {
    const codename = this.product.tier;
    const productName = getProductName({ codename }, this.i18n);
    this.notify.success(
      this.i18n.translate('app.pricing.product_order.order_completed_toast', { productName }),
    );
    this.dialog.close();
  }

  private extractErrorMessage(error: any): string {
    if (error.message instanceof Array) {
      return error.message[0];
    } else {
      return getErrorMessage(error);
    }
  }

  // eslint-disable-next-line
  private onContextUpdate({
    params,
    model,
    billingPlans,
    activeBillingPlan,
    company,
    estimate,
    subscription,
    sizeRange,
  }: Context) {
    const { productId, quantity, slots, interval } = params;
    const product = billingPlans.find(p => p.id === productId);

    if (!product) {
      throw new Error(`BillingPlan with id "${productId}" not found`);
    }

    this.product = product;
    this.isPP24Checkout = isPp24(product.tier);
    this.price = quantity
      ? this.product.prices.find(
          price =>
            (price.fteRangeEnd !== null
              ? price.fteRangeStart <= quantity && price.fteRangeEnd >= quantity
              : price.fteRangeStart <= quantity) && price.billingPeriodMonths === interval,
        )
      : undefined;
    this.sizeRange = sizeRange;
    this.billingInformation = model.billingInformation || {};
    this.slots = slots;
    this.interval = interval;
    this.hasCreditCardData = !!(
      this.billingInformation &&
      this.billingInformation.stripeCardExpiration &&
      this.billingInformation.stripeCardLast4
    );
    this.subscription = subscription;
    this.isTrial = !!model.trial;
    const isManualPlanChange = !!model.subscription?.manual && this.isPP24Checkout;
    this.manualChangeMode = isManualPlanChange
      ? estimate.amountDue === 0
        ? 'renewal'
        : 'upgrade'
      : null;
    this.payLaterEnabled =
      this.isPP24Checkout && subscription && subscription.manual && !subscription.autoCollection;
    this.activeBillingPlan = activeBillingPlan;

    this.companyName = company.name;
    this.companyId = company.id;
    this.billingEstimate = estimate;
    this.isDowngrade = !!subscription && !model.trial && this.downgradeScreenViewed();

    this.setViewText();
    this.setProductCost();
    this.setConfirmations();
    this.updateState();
  }

  private updateState() {
    this.flow = this.getFlow();
    this.breadcrumbs = this.getBreadcrumbs();
    this.cdRef.detectChanges();
  }

  private getFlow(currentFlow = this.flow): PaymentMethodFlow {
    if (!currentFlow) {
      if (this.hasCreditCardData) {
        return 'reuse';
      } else {
        return 'pristine';
      }
    }

    if (currentFlow === 'pristine' && this.hasCreditCardData) {
      return 'reuse';
    }

    return currentFlow;
  }

  private getBreadcrumbs(): Breadcrumb[] {
    return [
      {
        name: this.i18n.translate('app.pricing.product_order.breadcrumb_plan_picker'),
        url: [ProductOrderStepUrl.ProductPicker],
        disabled: false,
        icon: 'tag-text-outline',
      },
      {
        name: this.i18n.translate('app.pricing.product_order.breadcrumb_billing'),
        url: ['../', ProductOrderStepUrl.OrderBilling],
        disabled: false,
        icon: 'short-text',
      },
      {
        name: this.i18n.translate('app.pricing.product_order.breadcrumb_payment'),
        url: ['../', ProductOrderStepUrl.OrderPayment],
        disabled: false,
        icon: 'credit-card',
      },
    ];
  }

  private downgradeScreenViewed(): boolean {
    const { downgradeScreenViewed } = <{ downgradeScreenViewed?: boolean; navigationId?: number }>(
      this.location.getState()
    );

    return !!downgradeScreenViewed;
  }

  private migrationScreenViewed(): boolean {
    const { migrationScreenViewed } = <{ migrationScreenViewed?: boolean; navigationId?: number }>(
      this.location.getState()
    );

    return !!migrationScreenViewed;
  }

  private setProductCost() {
    if (this.isPP24Checkout && this.price) {
      this.productCost = get2024ProductCosts(
        this.product.prices,
        this.subscription!.addons,
        this.price.fteRangeStart,
        this.interval,
      );
    } else if (this.slots !== undefined) {
      this.productCost = getProductCost(
        this.product,
        this.slots,
        this.interval,
        this.subscription!.addons,
      );
    }
  }

  private setViewText(): void {
    const downgrade = !!this.subscription && !this.isTrial && this.downgradeScreenViewed();

    this.ctaBtnText = this.i18n.translate(
      downgrade ? 'app.pricing.product_order.downgrade_cta' : 'app.pricing.product_order.pay_now',
    );
    this.altCtaBtnText = this.payLaterEnabled
      ? this.i18n.translate('app.pricing.product_order.pay_later')
      : '';

    this.headingText = this.i18n.translate(
      downgrade
        ? 'app.pricing.product_order.downgrade_cta'
        : 'app.pricing.product_order.step_header_payment',
    );
    this.manualTermsGeneral = this.getManualTermsGeneral();
    this.additionalTermsText = this.getAdditionalTermsText();
  }

  private setConfirmations(estimate: BillingEstimate = this.billingEstimate) {
    const subscriptionCost = formatPrice(
      this.productCost,
      this.locale,
      this.product.currency,
      '1.0-0',
    );
    const amountDue = formatPrice(
      (estimate.amountDue || 0) / 100,
      this.locale,
      this.product.currency,
    );
    this.confirmations = this.manualChangeMode
      ? [
          {
            value: false,
            label: this.i18n.translate(
              'app.pricing.product_order.payment_footer_manual_terms.representation_confirmed',
            ),
          },
          {
            value: false,
            label: this.getManualMigrationConfirmation(amountDue, subscriptionCost, estimate),
          },
          {
            value: false,
            label: this.i18n.translate(
              'app.pricing.product_order.payment_footer_manual_terms.not_an_agency_confirmed',
            ),
          },
        ]
      : [
          {
            value: false,
            label: this.getDefaultConfirmation(estimate),
          },
        ];
  }

  private getDefaultConfirmation(
    estimate: BillingEstimate = this.billingEstimate,
    downgrade: boolean = this.isDowngrade,
  ) {
    const isChangingSlotsOnly =
      !!this.activeBillingPlan &&
      this.activeBillingPlan.tier === this.product.tier &&
      this.activeBillingPlan.billingPeriod === +this.interval;
    const subscriptionCost = formatPrice(
      this.productCost,
      this.locale,
      this.product.currency,
      '1.0-0',
    );
    const amountDue = formatPrice(
      (estimate.amountDue || 0) / 100,
      this.locale,
      this.product.currency,
    );
    const messageParts: string[] = [];

    if (estimate.renewalPrice !== null) {
      messageParts.push(this.getRenewalPriceMessagePart(amountDue, estimate));
    } else if (estimate.prorated) {
      messageParts.push(
        ...this.getProratedMessageParts(amountDue, subscriptionCost, estimate, downgrade),
      );
    } else if (isChangingSlotsOnly) {
      messageParts.push(this.getChangingSlotsMessagePart(amountDue));
    } else {
      messageParts.push(...this.getDefaultChargeMessageParts(amountDue));
    }

    messageParts.push(this.getTermsMessagePart());

    return messageParts.join(' ');
  }

  private getManualMigrationConfirmation(
    amountDue: string,
    subscriptionCost: string,
    estimate: BillingEstimate,
  ) {
    const billingPeriod = this.getIntervalTranslation(this.manualChangeMode === 'renewal');
    const nextBillingAt = moment(
      this.manualChangeMode === 'upgrade'
        ? estimate.nextBillingAt
        : this.subscription?.contractTerm?.contractEnd || estimate.nextBillingAt,
    ).format('DD MMMM YYYY');
    return this.i18n.translate(
      this.manualChangeMode === 'upgrade'
        ? 'app.pricing.product_order.payment_footer_manual_terms.amount_confirm_upgrade'
        : 'app.pricing.product_order.payment_footer_manual_terms.amount_confirm_renewal',
      {
        amountDue,
        subscriptionCost,
        billingPeriod,
        nextBillingAt,
      },
    );
  }

  private getManualTermsGeneral() {
    if (!this.manualChangeMode) return '';
    return this.i18n.translate(
      this.payLaterEnabled
        ? 'app.pricing.product_order.payment_footer_manual_terms.general_alt'
        : 'app.pricing.product_order.payment_footer_manual_terms.general',
      {
        buttonText: this.ctaBtnText,
        altButtonText: this.altCtaBtnText,
      },
    );
  }

  private getAdditionalTermsText() {
    if (!this.manualChangeMode) return '';
    let key = '';
    if (this.manualChangeMode === 'upgrade') {
      if (this.payLaterEnabled) {
        key = 'app.pricing.product_order.payment_footer_manual_terms.general_expanded_upgrade_alt';
      } else {
        key = 'app.pricing.product_order.payment_footer_manual_terms.general_expanded_upgrade';
      }
    } else {
      if (this.payLaterEnabled) {
        key = 'app.pricing.product_order.payment_footer_manual_terms.general_expanded_renewal_alt';
      } else {
        key = 'app.pricing.product_order.payment_footer_manual_terms.general_expanded_renewal';
      }
    }
    return this.i18n.translate(key, {
      buttonText: this.ctaBtnText,
      altButtonText: this.altCtaBtnText,
      nextBillingAt: moment(
        this.subscription?.contractTerm?.contractEnd || this.billingEstimate.nextBillingAt,
      ).format('DD MMMM YYYY'),
    });
  }

  private getRenewalPriceMessagePart(amountDue: string, estimate: BillingEstimate) {
    return this.i18n.translate(
      'app.pricing.product_order.payment_footer_amount_due.today_with_sub_cost',
      {
        amountDue,
        subscriptionCost: formatPrice(
          (estimate.renewalPrice || 0) / 100,
          this.locale,
          this.product.currency,
        ),
        billingPeriod: this.getIntervalTranslation(),
        nextBillingAt: moment(estimate.nextBillingAt).format('DD MMMM YYYY'),
        buttonText: this.ctaBtnText,
      },
    );
  }

  private getProratedMessageParts(
    amountDue: string,
    subscriptionCost: string,
    estimate: BillingEstimate,
    downgrade: boolean,
  ) {
    const parts: string[] = [];
    const options = {
      amountDue,
      subscriptionCost,
      billingPeriod: this.getIntervalTranslation(),
      nextBillingAt: moment(estimate.nextBillingAt).format('DD MMMM YYYY'),
    };
    parts.push(
      this.i18n.translate(
        'app.pricing.product_order.payment_footer_amount_due.today_with_sub_cost_excl_tax',
        options,
      ),
    );
    if (!!this.maxEmployees) {
      parts.push(
        this.i18n.translate('app.pricing.product_order.payment_footer_max_employees', {
          quantity: this.maxEmployees,
        }),
      );
    }
    if (this.isPP24Checkout) {
      if (downgrade) {
        parts.push(
          this.i18n.translate('app.pricing.product_order.payment_footer_charges.on_renewal'),
        );
      } else {
        parts.push(this.i18n.translate('app.pricing.product_order.payment_footer_charges.now'));
      }
    }
    return parts;
  }

  private getChangingSlotsMessagePart(amountDue: string) {
    return this.i18n.translate(
      'app.pricing.product_order.payment_footer_amount_due.charged_today',
      {
        cost: amountDue,
      },
    );
  }

  private getDefaultChargeMessageParts(amountDue: string) {
    const parts: string[] = [];
    parts.push(
      this.i18n.translate('app.pricing.product_order.payment_footer_amount_due.charged_interval', {
        cost: amountDue,
        interval: this.getIntervalTranslation(),
      }),
    );
    if (!!this.maxEmployees) {
      parts.push(
        this.i18n.translate('app.pricing.product_order.payment_footer_max_employees', {
          quantity: this.maxEmployees,
        }),
      );
    }
    return parts;
  }

  private getTermsMessagePart() {
    return this.i18n.translate(
      this.isPP24Checkout
        ? 'app.pricing.product_order.payment_footer_terms.no_agencies'
        : 'app.pricing.product_order.payment_footer_terms.default',
      {
        buttonText: this.ctaBtnText,
      },
    );
  }

  private getIntervalTranslation(short: boolean = false) {
    const remainder = +this.interval % 12;
    const numberOfYears = +this.interval / 12;
    const isXYearInterval = numberOfYears > 1 && remainder === 0;

    switch (this.interval.toString()) {
      case 'year':
      case '12':
        return this.i18n.translate(
          short
            ? 'app.pricing.payment_interval_short.yearly'
            : 'app.pricing.product_order.payment_info_once_x_time.year',
        );
      case 'month':
      case '1':
        return this.i18n.translate(
          short
            ? 'app.pricing.payment_interval_short.monthly'
            : 'app.pricing.product_order.payment_info_once_x_time.month',
        );

      default:
        return isXYearInterval
          ? this.i18n.translate('app.pricing.product_order.payment_info_once_x_time.x_year', {
              interval: numberOfYears,
            })
          : this.i18n.translate('app.pricing.product_order.payment_info_once_x_time.x_month', {
              interval: this.interval,
            });
    }
  }

  private handleErrors() {
    merge(
      this.actions$.pipe(
        ofType(
          SubscriptionPricingActions.paymentIntentFailure,
          SubscriptionPricingActions.changePlanFailure,
        ),
      ),
      this.actions$.pipe(
        ofType(
          SubscriptionPricingActions.patchSubscriptionFailure,
          SubscriptionPricingActions.paymentIntentStripeFailure,
          SubscriptionPricingActions.addCardFailure,
        ),
        map(({ payload }) => payload.error),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(err => this.onError(err));
  }
}
